import { useLazyQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { GET_ORDERS_FOR_ACCOUNT, GET_ORDERS_FOR_CONTACT } from 'utils/graphql';
import { formatCurrency } from 'utils/text.util';

const OrdersDatatable = ({
    accountId,
    contactId = null,
    isB2C = false,
    setIsOrdersTableLoaded,
}) => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('effectivedate');
    const [orderByDirection, setOrderByDirection] = useState('desc');

    const firstUpdate = useRef(true);

    const [getOrdersForAccount] = useLazyQuery(GET_ORDERS_FOR_ACCOUNT, {
        fetchPolicy: 'cache-and-network',
    });

    const [getOrdersForContact] = useLazyQuery(GET_ORDERS_FOR_CONTACT, {
        fetchPolicy: 'cache-and-network',
    });

    const columns = [
        {
            id: 'effectivedate',
            sortable: true,
            name: t('date').toUpperCase(),
            selector: (row) => {
                return new Date(row.effectivedate + 'Z').toISOString().slice(0, 10);
            },
        },
        {
            id: 'cards',
            sortable: false,
            name: t('cards').toUpperCase(),
            selector: (row) => {
                if (row.orderitem_list && row.orderitem_list.length > 0) {
                    let quantity = 0;
                    row.orderitem_list.forEach((item) => {
                        quantity += Number(item.orderitem.quantity);
                    });
                    return quantity;
                }
                return 1;
            },
        },
        {
            id: 'totalamount',
            sortable: true,
            name: t('total').toUpperCase(),
            selector: (row) => {
                return '$' + formatCurrency(row.totalamount);
            },
        },
        {
            id: 'location',
            sortable: false,
            name: t('location').toUpperCase(),
            selector: (row) => {
                if (row.property__c_list && row.property__c_list.length > 0)
                    return row.property__c_list[0].property__c.name;
                return 'N/A';
            },
        },
        {
            id: 'contact',
            sortable: false,
            name: t('contact').toUpperCase(),
            selector: (row) => {
                if (row.contact_list && row.contact_list.length > 0)
                    return row.contact_list[0].contact.name;
                return 'N/A';
            },
        },
        {
            id: 'ordernumber',
            sortable: false,
            name: t('order_number_short').toUpperCase(),
            selector: (row) => {
                if (row.external_id__c && row.ecomm) {
                    return row.external_id__c;
                }
                return row.vts_id;
            },
        },
    ];

    const loadOrders = () => {
        setIsLoading(true);
        if (isB2C) {
            getOrdersForContact({
                variables: {
                    accountid: accountId,
                    contactid: contactId,
                    offset: (page - 1) * rowsPerPage,
                    limit: rowsPerPage,
                    orderBy,
                    orderByDirection: orderByDirection.toUpperCase(),
                },
                onCompleted: (data) => {
                    setTotalOrders(data.table.count);
                    setOrders(data.table.records.map((record) => record.order));
                    if (firstUpdate.current) {
                        firstUpdate.current = false;
                    }
                    setIsLoading(false);
                    setIsOrdersTableLoaded(true);
                },
            });
        } else {
            getOrdersForAccount({
                variables: {
                    accountid: accountId,
                    offset: (page - 1) * rowsPerPage,
                    limit: rowsPerPage,
                    orderBy,
                    orderByDirection: orderByDirection.toUpperCase(),
                },
                onCompleted: (data) => {
                    setTotalOrders(data.table.count);
                    setOrders(data.table.records.map((record) => record.order));
                    if (firstUpdate.current) {
                        firstUpdate.current = false;
                    }
                    setIsLoading(false);
                    setIsOrdersTableLoaded(true);
                },
            });
        }
    };

    const onSort = (selectedColumn, sortDirection, sortedRows) => {
        if (selectedColumn && selectedColumn.id) {
            setOrderBy(selectedColumn.id);
            setOrderByDirection(sortDirection);
        }
    };

    useEffect(() => {
        if (!accountId) return;
        loadOrders();
    }, [accountId]);

    useEffect(() => {
        if (!accountId || !isB2C) return;
        loadOrders();
    }, [contactId]);

    useEffect(() => {
        if (firstUpdate.current) return;
        loadOrders();
    }, [page, rowsPerPage, orderBy, orderByDirection]);

    return (
        <div className="orders-datatable mb-4">
            <DataTable
                persistTableHead
                columns={columns}
                data={orders}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                paginationTotalRows={totalOrders}
                progressPending={isLoading}
                progressComponent={
                    <div className="d-flex justify-content-center align-items-center my-4">
                        <Spinner></Spinner>
                    </div>
                }
                sortServer
                onSort={(selectedColumn, sortDirection, sortedRows) =>
                    onSort(selectedColumn, sortDirection, sortedRows)
                }
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage) => setRowsPerPage(currentRowsPerPage)}
                paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
            />
        </div>
    );
};

export default OrdersDatatable;
